<script setup>
defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["view", "edit"]);

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const statusText = (status) => {
  const map = {
    pending: "รอชำระ",
    partial: "ชำระบางส่วน",
    paid: "ชำระแล้ว",
    overdue: "ค้างชำระ",
  };
  return map[status] || status;
};

const statusBadgeClass = (status) => {
  const map = {
    pending: "text-bg-secondary",
    partial: "text-bg-warning",
    paid: "text-bg-success",
    overdue: "text-bg-danger",
  };
  return map[status] || "text-bg-light";
};

const escapeCSV = (value) => {
  const str = String(value ?? "");
  return `"${str.replace(/"/g, '""')}"`;
};

const exportCSV = (items) => {
  if (!items.length) return;

  const headers = [
    "เลขที่บิล",
    "อาคาร",
    "รอบบิล",
    "ยูนิต",
    "ลูกบ้าน",
    "วันที่ออกบิล",
    "ครบกำหนด",
    "ยอดสุทธิ",
    "ชำระแล้ว",
    "สถานะ",
  ];

  const rows = items.map((invoice) => [
    invoice.invoiceNo,
    invoice.building,
    invoice.billingMonth,
    invoice.unitCode,
    invoice.residentName,
    formatDate(invoice.issueDate),
    formatDate(invoice.dueDate),
    invoice.totalAmount || 0,
    invoice.paidAmount || 0,
    statusText(invoice.status),
  ]);

  const csvContent = [
    headers.map(escapeCSV).join(","),
    ...rows.map((row) => row.map(escapeCSV).join(",")),
  ].join("\n");

  // ใส่ BOM เพื่อให้ Excel เปิดภาษาไทยได้ถูกต้อง
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);

  link.href = url;
  link.setAttribute("download", `invoices-${today}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
</script>

<template>
  <div class="card border-0 shadow-sm rounded-4">
    <div class="card-body p-0">
      <div
        class="px-4 pt-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <div>
          <h5 class="fw-bold mb-1">รายการใบแจ้งหนี้</h5>
          <div class="text-muted small">
            พบทั้งหมด {{ items.length }} รายการ
          </div>
        </div>

        <button
          type="button"
          class="btn btn-outline-danger border-danger-subtle rounded-3 shadow-sm"
          :disabled="!items.length"
          @click="exportCSV(items)"
        >
          <i class="bi bi-download me-1"></i>Export
        </button>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">เลขที่บิล</th>
              <th>รอบบิล</th>
              <th>ยูนิต</th>
              <th>ลูกบ้าน</th>
              <th>วันที่ออกบิล</th>
              <th>ครบกำหนด</th>
              <th class="text-end">ยอดสุทธิ</th>
              <th class="text-end">ชำระแล้ว</th>
              <th>สถานะ</th>
              <th class="text-center pe-4">จัดการ</th>
            </tr>
          </thead>

          <tbody v-if="items.length">
            <tr v-for="invoice in items" :key="invoice.id">
              <td class="ps-4">
                <div class="fw-semibold">{{ invoice.invoiceNo }}</div>
                <div class="small text-muted">{{ invoice.building }}</div>
              </td>
              <td>{{ invoice.billingMonth }}</td>
              <td>{{ invoice.unitCode }}</td>
              <td>{{ invoice.residentName }}</td>
              <td>{{ formatDate(invoice.issueDate) }}</td>
              <td>{{ formatDate(invoice.dueDate) }}</td>
              <td class="text-end fw-semibold">
                {{ formatCurrency(invoice.totalAmount) }}
              </td>
              <td class="text-end text-success fw-semibold">
                {{ formatCurrency(invoice.paidAmount) }}
              </td>
              <td>
                <span
                  class="badge rounded-pill"
                  :class="statusBadgeClass(invoice.status)"
                >
                  {{ statusText(invoice.status) }}
                </span>
              </td>
              <td class="text-center pe-4">
                <div class="btn-group">
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    title="ดูรายละเอียด"
                    @click="$emit('view', invoice)"
                  >
                    <i class="bi bi-eye text-primary-emphasis"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    title="แก้ไข"
                    @click="$emit('edit', invoice)"
                  >
                    <i class="bi bi-pencil-square text-warning-emphasis"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="10" class="text-center py-5">
                <div class="text-muted">
                  <i class="bi bi-receipt fs-2 d-block mb-2"></i>
                  ไม่พบข้อมูลใบแจ้งหนี้ตามเงื่อนไขที่ค้นหา
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table th {
  white-space: nowrap;
  font-size: 0.9rem;
}
</style>
